/* eslint-disable max-lines, react/no-array-index-key */
import { Fragment, useEffect, useRef, useState } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NoteOutlinedIcon from '@mui/icons-material/NoteOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { isEmpty, isNil, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { getStateDisplayName } from 'store/utils/shipmentUtils';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import RichTextViewer from 'common/components/RichTextViewer';
import useRoles from 'common/hooks/useRoles';
import { listReceivingsByPage } from 'store/thunks/stockManagementThunk';
import AddLineDialog from './AddLineDialog';
import LineSplitsDialog from './LineSplitsDialog';

const PRIMARY = '#031c57';
const PRIMARY_DARK = '#021544';
const PRIMARY_LIGHT = '#eef1f8';
const ROW_EVEN = '#dce6f5';
const ROW_ODD = '#eef3fb';

const STATUS_STYLES = {
  PENDING_VERIFICATION: { bgcolor: '#ed6c02', color: '#fff' },
  VERIFIED: { bgcolor: '#2e7d32', color: '#fff' },
  COMPLETED: { bgcolor: '#2e7d32', color: '#fff' },
  REJECTED: { bgcolor: '#d32f2f', color: '#fff' },
};

const COLUMN_COUNT = 9;

const headCellSx = {
  color: '#fff',
  fontWeight: 700,
  borderBottom: `1px solid rgba(255,255,255,0.15)`,
  py: 1.5,
};

const rowCellSx = {
  color: '#212121',
  borderBottom: `1px solid #d0daea`,
  py: 1.25,
};

const StockManagementTable = () => {
  const dispatch = useDispatch();
  // Adding a line to a receipt is the same server operation as creating one
  // (upsertInventoryReceiving), which is Admin only. Without this the dialog opened for an
  // Inventory Manager and refused on save, after the whole line had been typed in.
  const hasAccess = useRoles();
  const { pages, currentPage, totalCount } = useSelector(
    ({ inventory }) => inventory.stockManagement
  );
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [splitsLine, setSplitsLine] = useState(null);
  const [addLineReceiving, setAddLineReceiving] = useState(null);
  const [notesPopover, setNotesPopover] = useState({ anchorEl: null, notes: '', id: null });
  const closeTimerRef = useRef(null);

  const handleNotesEnter = (e, row) => {
    clearTimeout(closeTimerRef.current);
    setNotesPopover({ anchorEl: e.currentTarget, notes: row.notes, id: row.receivingId });
  };

  const handleNotesLeave = () => {
    closeTimerRef.current = setTimeout(
      () => setNotesPopover({ anchorEl: null, notes: '', id: null }),
      150
    );
  };

  const totalPages = Math.ceil(totalCount / 25);
  const receivings = pages[currentPage];

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) return;
    dispatch(listReceivingsByPage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyId = (e, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
  };

  useEffect(() => {
    dispatch(listReceivingsByPage({ page: 1 }));
  }, [dispatch]);

  return (
    <>
      <Grid
        component={Paper}
        sx={{ width: '100%', overflow: 'hidden' }}
        container
        direction='column'
      >
        <Grid item>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: PRIMARY_DARK }}>
                  <TableCell sx={headCellSx} />
                  <TableCell sx={headCellSx}>Receiving ID</TableCell>
                  <TableCell sx={headCellSx}>Vendor Name</TableCell>
                  <TableCell sx={headCellSx}>Manifest Ref</TableCell>
                  <TableCell sx={headCellSx}>Received Date</TableCell>
                  <TableCell sx={headCellSx}>Facility</TableCell>
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <TableCell sx={headCellSx} />
                  <TableCell sx={headCellSx}>Created At</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {map(receivings, (row, idx) => {
                  const isExpanded = expandedRows.has(row.receivingId);
                  const rowBg = idx % 2 === 0 ? ROW_EVEN : ROW_ODD;

                  return (
                    <Fragment key={row.receivingId}>
                      <TableRow
                        sx={{
                          backgroundColor: isExpanded ? '#c9d9ef' : rowBg,
                          '&:hover': { backgroundColor: '#c9d9ef', cursor: 'pointer' },
                          transition: 'background-color 0.15s',
                        }}
                        onClick={() => toggleRow(row.receivingId)}
                      >
                        <TableCell sx={{ ...rowCellSx, width: 50 }}>
                          <IconButton
                            size='small'
                            aria-label={`Toggle details ${row.receivingId}`}
                            sx={{ color: PRIMARY }}
                          >
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>

                        <TableCell sx={rowCellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                            <Tooltip title={row.receivingId} placement='right' arrow>
                              <Box component='span' sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                {row.receivingId.split('-')[0]}…
                              </Box>
                            </Tooltip>
                            <IconButton
                              size='small'
                              onClick={(e) => handleCopyId(e, row.receivingId)}
                              sx={{ p: 0.25, ml: 0.25, color: 'text.secondary' }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Box>
                        </TableCell>

                        <TableCell sx={rowCellSx}>{row.vendorName}</TableCell>
                        <TableCell sx={rowCellSx}>{row.manifestReference}</TableCell>
                        <TableCell sx={rowCellSx}>
                          {row.receivedDate
                            ? format(new Date(row.receivedDate), 'MM/dd/yyyy')
                            : '—'}
                        </TableCell>
                        <TableCell sx={rowCellSx}>
                          {row.receivedAtPharmacy ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                              <Box component='span'>{row.receivedAtPharmacy.name}</Box>
                              <Box component='span' sx={{ fontSize: 12, color: 'text.secondary' }}>
                                {getStateDisplayName(row.receivedAtPharmacy.state)}
                              </Box>
                            </Box>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell sx={rowCellSx}>
                          <Chip
                            label={row.status.replace(/_/g, ' ')}
                            size='small'
                            sx={STATUS_STYLES[row.status] ?? { bgcolor: 'grey.500', color: '#fff' }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...rowCellSx, width: 40, p: 0.5 }}>
                          {row.notes && (
                            <IconButton
                              size='small'
                              sx={{ color: PRIMARY }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                handleNotesEnter(e, row);
                              }}
                              onMouseLeave={handleNotesLeave}
                              onClick={(e) => e.stopPropagation()}
                              aria-label='View notes'
                            >
                              <NoteOutlinedIcon fontSize='small' />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell sx={rowCellSx}>
                          {format(new Date(row.createdAt), 'MM/dd/yyyy hh:mm a')}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, border: 'none' }}>
                          <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                            <Box
                              sx={{
                                py: 2,
                                px: 5,
                                backgroundColor: PRIMARY_LIGHT,
                                borderLeft: `4px solid ${PRIMARY}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  mb: 1,
                                }}
                              >
                                {hasAccess.createReceivingOrder && (
                                  <Button
                                    size='small'
                                    startIcon={<AddIcon />}
                                    variant='outlined'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddLineReceiving(row);
                                    }}
                                  >
                                    Add Line
                                  </Button>
                                )}
                              </Box>

                              {isEmpty(row.lines) ? (
                                <NothingFound
                                  height='80px'
                                  customMessage='No items found'
                                  hideBox
                                />
                              ) : (
                                <Table size='small'>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: PRIMARY }}>
                                      {[
                                        'NDC',
                                        'Manufacturer',
                                        'Lot #',
                                        'BUD',
                                        'Qty Ordered',
                                        'Qty Shipped',
                                        'Qty Received',
                                        'Distribution',
                                        'splits-action',
                                      ].map((header) => (
                                        <TableCell
                                          key={header}
                                          sx={{ ...headCellSx, fontWeight: 600 }}
                                        >
                                          {header === 'splits-action' ? '' : header}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {map(row.lines, (line, lineIdx) => {
                                      const lineOffage =
                                        line.quantityShipped != null &&
                                        line.totalQuantityReceived != null
                                          ? Math.max(
                                              0,
                                              Number(line.quantityShipped) -
                                                Number(line.totalQuantityReceived)
                                            )
                                          : 0;
                                      const hasOffageReasons =
                                        lineOffage > 0 &&
                                        line.offageReasons &&
                                        line.offageReasons.length > 0;
                                      // Every line carries one auto-verified split for the
                                      // receiving pharmacy itself, so only a count above that
                                      // means the line was actually distributed. Undistributed
                                      // lines get no chip — the eye should land on the lines
                                      // that still need someone to act.
                                      const splitCount = line.splitCount ?? 0;
                                      const pendingSplitCount = line.pendingSplitCount ?? 0;
                                      const hasDistributions = splitCount > 1;
                                      const isLinePending = pendingSplitCount > 0;
                                      return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <TableRow
                                          key={lineIdx}
                                          sx={{
                                            backgroundColor: lineIdx % 2 === 0 ? '#fff' : '#f5f7fb',
                                          }}
                                        >
                                          <TableCell>{line.ndc ?? '—'}</TableCell>
                                          <TableCell>{line.manufacturer ?? '—'}</TableCell>
                                          <TableCell>{line.lotNumber ?? '—'}</TableCell>
                                          <TableCell>
                                            {line.bud
                                              ? format(new Date(line.bud), 'MM/dd/yyyy')
                                              : '—'}
                                          </TableCell>
                                          <TableCell>{line.quantityOrdered ?? '—'}</TableCell>
                                          <TableCell>{line.quantityShipped ?? '—'}</TableCell>
                                          <TableCell>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                              }}
                                            >
                                              <Typography variant='body2'>
                                                {line.totalQuantityReceived ?? '—'}
                                              </Typography>
                                              {lineOffage > 0 &&
                                                (hasOffageReasons ? (
                                                  <Tooltip
                                                    placement='top'
                                                    arrow
                                                    title={
                                                      <Box sx={{ p: 0.5 }}>
                                                        <Typography
                                                          variant='caption'
                                                          sx={{
                                                            fontWeight: 700,
                                                            color: '#ed6c02',
                                                            display: 'block',
                                                            mb: 0.75,
                                                          }}
                                                        >
                                                          {lineOffage} unit
                                                          {lineOffage !== 1 ? 's' : ''} offage
                                                        </Typography>
                                                        {line.offageReasons.map((r, rIdx) => (
                                                          // eslint-disable-next-line react/no-array-index-key
                                                          <Box
                                                            key={rIdx}
                                                            sx={{
                                                              display: 'flex',
                                                              gap: 0.75,
                                                              mb: 0.5,
                                                            }}
                                                          >
                                                            <Chip
                                                              label={`Qty ${r.qty}`}
                                                              size='small'
                                                              sx={{
                                                                height: 18,
                                                                fontSize: '0.65rem',
                                                                bgcolor: '#ed6c02',
                                                                color: '#fff',
                                                                fontWeight: 700,
                                                                flexShrink: 0,
                                                              }}
                                                            />
                                                            <Typography
                                                              variant='caption'
                                                              color='text.secondary'
                                                              sx={{ lineHeight: 1.4 }}
                                                            >
                                                              {r.reasons.join(' · ')}
                                                            </Typography>
                                                          </Box>
                                                        ))}
                                                      </Box>
                                                    }
                                                    componentsProps={{
                                                      tooltip: {
                                                        sx: {
                                                          bgcolor: 'background.paper',
                                                          color: 'text.primary',
                                                          border: '1px solid #ffd18a',
                                                          borderLeft: '3px solid #ed6c02',
                                                          boxShadow: 3,
                                                          '& .MuiTooltip-arrow': {
                                                            color: '#ffd18a',
                                                          },
                                                          maxWidth: 300,
                                                          p: 1,
                                                        },
                                                      },
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.25,
                                                        cursor: 'pointer',
                                                      }}
                                                    >
                                                      <Typography
                                                        variant='caption'
                                                        sx={{ color: '#ed6c02', fontWeight: 700 }}
                                                      >
                                                        -{lineOffage}
                                                      </Typography>
                                                      <WarningAmberIcon
                                                        sx={{ fontSize: 14, color: '#ed6c02' }}
                                                      />
                                                    </Box>
                                                  </Tooltip>
                                                ) : (
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 0.25,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant='caption'
                                                      sx={{ color: '#ed6c02', fontWeight: 700 }}
                                                    >
                                                      -{lineOffage}
                                                    </Typography>
                                                    <WarningAmberIcon
                                                      sx={{ fontSize: 14, color: '#ed6c02' }}
                                                    />
                                                  </Box>
                                                ))}
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            {hasDistributions && (
                                              <Chip
                                                label={
                                                  isLinePending
                                                    ? 'PENDING VERIFICATION'
                                                    : 'VERIFIED'
                                                }
                                                size='small'
                                                aria-label={
                                                  isLinePending
                                                    ? `${pendingSplitCount} of ${splitCount} distributions pending verification`
                                                    : `all ${splitCount} distributions verified`
                                                }
                                                sx={
                                                  isLinePending
                                                    ? STATUS_STYLES.PENDING_VERIFICATION
                                                    : STATUS_STYLES.VERIFIED
                                                }
                                              />
                                            )}
                                          </TableCell>
                                          <TableCell sx={{ width: 40, pr: 1 }}>
                                            <Tooltip
                                              title='View distribution'
                                              placement='left'
                                              arrow
                                            >
                                              <IconButton
                                                size='small'
                                                sx={{ color: PRIMARY }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setSplitsLine({
                                                    line,
                                                    receivedAtPharmacyId:
                                                      row.receivedAtPharmacy
                                                        ?.fulfillingPharmacyId ?? null,
                                                  });
                                                }}
                                                aria-label='View distribution'
                                              >
                                                <AddCircleOutlineIcon fontSize='small' />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>

              {!isNil(receivings) && totalPages > 1 && (
                <TableFooter>
                  <TableRow sx={{ backgroundColor: PRIMARY_DARK }}>
                    <TableCell colSpan={COLUMN_COUNT} align='right' sx={{ borderBottom: 'none' }}>
                      <Pagination
                        sx={{
                          justifyContent: 'flex-end',
                          display: 'flex',
                          '& .MuiPaginationItem-root': { color: '#fff' },
                          // Default selected tint is invisible on the dark footer; invert it.
                          '& .MuiPaginationItem-root.Mui-selected, & .MuiPaginationItem-root.Mui-selected:hover':
                            {
                              backgroundColor: '#fff',
                              color: PRIMARY_DARK,
                              fontWeight: 700,
                            },
                        }}
                        count={totalPages}
                        page={currentPage}
                        size='small'
                        onChange={handlePageChange}
                      />
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        </Grid>

        {isNil(receivings) && (
          <Grid item>
            <LoadingModule />
          </Grid>
        )}

        {!isNil(receivings) && isEmpty(receivings) && (
          <Grid item>
            <NothingFound />
          </Grid>
        )}
      </Grid>

      <LineSplitsDialog
        line={splitsLine?.line}
        receivedAtPharmacyId={splitsLine?.receivedAtPharmacyId}
        open={Boolean(splitsLine)}
        onClose={() => setSplitsLine(null)}
      />

      <AddLineDialog
        receivingId={addLineReceiving?.receivingId}
        receivedAtPharmacyId={addLineReceiving?.receivedAtPharmacy?.fulfillingPharmacyId}
        open={Boolean(addLineReceiving)}
        onClose={() => setAddLineReceiving(null)}
      />

      <Popover
        open={Boolean(notesPopover.anchorEl)}
        anchorEl={notesPopover.anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        disableRestoreFocus
        disableAutoFocus
        sx={{ pointerEvents: 'none' }}
        PaperProps={{
          onMouseEnter: () => clearTimeout(closeTimerRef.current),
          onMouseLeave: handleNotesLeave,
          sx: { pointerEvents: 'auto', maxWidth: 360, p: 1.5 },
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}
        >
          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Notes
          </Typography>
          <Tooltip title='Copy notes' arrow>
            <IconButton
              size='small'
              onClick={() => navigator.clipboard.writeText(notesPopover.notes)}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {notesPopover.id && (
          <RichTextViewer value={notesPopover.notes} id={`notes-popover-${notesPopover.id}`} />
        )}
      </Popover>
    </>
  );
};

export default StockManagementTable;
